import { Frame, key, useWidgetText } from "../shared";
import type { WidgetProps } from "../types";
import { controlBindings, controlState, isPending } from "./control";
import { ControlNotes } from "./ControlNotes";

/** toggle: an on/off command or setting, showing the requested position while the device has not answered. */
export function Toggle({ widget, input, onAction, readOnly }: WidgetProps) {
  const fmt = useWidgetText();
  const { target, targetKey, action, point, device } = controlBindings(widget, input);
  const reported = point?.latest?.value;
  const pending = isPending(action);
  const shown = pending && typeof action?.value === "boolean" ? action.value : reported;
  const known = typeof shown === "boolean";
  const offline = device?.state === "offline";
  // Start and stop can be two commands with their own permission; the switch works only when both may be sent.
  const options = (widget.bindings ?? []).flatMap((b) => (b.command ? [b.command] : []));
  const allowed =
    (action?.allowed ?? false) &&
    options.every((k) => !target || input.actions?.[key(target.device, k)]?.allowed !== false);
  const state = controlState(action && { ...action, allowed }, typeof reported === "boolean", offline);
  const title = widget.title ?? action?.name ?? point?.name ?? "";
  const enabled = allowed && !pending && !readOnly;
  // The widget's own confirm; whether the chosen command is guarded is the page's to decide from the profile.
  const confirmFirst = widget.config?.confirm === true;
  // The page asks for confirmation (a dialog with the profile's text) and picks the command by value.
  function flip() {
    if (!target || !targetKey || !action) return;
    onAction?.({
      kind: action.kind,
      device: target.device,
      key: targetKey,
      value: !(shown === true),
      ...(confirmFirst ? { confirm: true } : {}),
      ...(options.length > 1 ? { options } : {}),
    });
  }
  return (
    <Frame widget={widget} state={state} title={title}>
      <div className="flex items-center justify-between gap-3">
        <span className="text-gray-900">
          {known ? fmt.t(shown ? "toggle.on" : "toggle.off") : fmt.t("state.noData")}
        </span>
        <button
          type="button"
          role="switch"
          aria-label={title}
          aria-checked={known ? shown : false}
          disabled={!enabled}
          onClick={flip}
          className="relative h-6 w-11 rounded-full bg-gray-300 transition-colors aria-checked:bg-brand disabled:opacity-50"
        >
          <span
            aria-hidden="true"
            className={`absolute top-0.5 size-5 rounded-full bg-white shadow transition-transform ${shown === true ? "translate-x-5" : "translate-x-0.5"}`}
          />
        </button>
      </div>
      <ControlNotes state={state} offline={offline} />
    </Frame>
  );
}
